import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useFetchTicketPriorities } from 'hooks/api/useTicketPriorities';
import { useChangePriority } from 'hooks/api/useTickets';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import type { ChangeTicketPriorityFormValuesT } from '../ChangeTicketPriorityPopup';

type UseChangeTicketPriorityPopupT = {
  isChangingPriority: boolean;
  ticketPrioritiesOptions: SelectOptionT[];
  isLoadingTicketPrioritiesOptions: boolean;
  handleChangeTicketPriority: (data: ChangeTicketPriorityFormValuesT, onClose: NoopT) => void;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useChangeTicketPriorityPopup = (): UseChangeTicketPriorityPopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  /**
   * Api hooks
   */
  const ticketPrioritiesResult = useFetchTicketPriorities(true);

  const changePriorityMutation = useChangePriority({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
  });

  /**
   * Variables
   */
  const ticketPrioritiesOptions = useMemo(
    () =>
      ticketPrioritiesResult.data
        ? ticketPrioritiesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [ticketPrioritiesResult.data]
  );

  /**
   * Handlers
   */
  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
    },
    []
  );

  const handleChangeTicketPriority = useCallback(
    (data: ChangeTicketPriorityFormValuesT, onClose: NoopT) => {
      changePriorityMutation.mutate(data, {
        onSuccess: () => {
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: 'Priority was changed successfully',
              snackBarSeverity: 'success',
            })
          );
          onClose();
        },
        onError: error => {
          const errorMsg =
            getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
              snackBarSeverity: 'error',
            })
          );
          onClose();
        },
      });
    },
    [changePriorityMutation, dispatch]
  );

  return {
    isChangingPriority: changePriorityMutation.isLoading,
    ticketPrioritiesOptions,
    isLoadingTicketPrioritiesOptions: ticketPrioritiesResult.isLoading,
    handleChangeTicketPriority,
    handleClosePopup,
  };
};

export default useChangeTicketPriorityPopup;
